import React, { memo, useCallback } from 'react';
import {
  MousePointer2,
  Type,
  PenLine,
  Calendar,
  User,
  CaseSensitive,
  Square,
  ImageIcon,
} from 'lucide-react';
import type { FieldToolId } from './FieldToolsBar';

export interface ToolRailItem {
  id: FieldToolId | 'image';
  label: string;
  shortcut?: string;
  icon: React.ReactNode;
}

const TOOLS: ToolRailItem[] = [
  { id: 'select', label: 'Select', shortcut: 'V', icon: <MousePointer2 size={20} strokeWidth={1.75} /> },
  { id: 'text', label: 'Text', shortcut: 'T', icon: <Type size={20} strokeWidth={1.75} /> },
  { id: 'signature-type', label: 'Signature', icon: <PenLine size={20} strokeWidth={1.75} /> },
  { id: 'date', label: 'Date', icon: <Calendar size={20} strokeWidth={1.75} /> },
  { id: 'name', label: 'Name', icon: <User size={20} strokeWidth={1.75} /> },
  { id: 'initials', label: 'Initials', icon: <CaseSensitive size={20} strokeWidth={1.75} /> },
  { id: 'checkbox', label: 'Check', icon: <Square size={20} strokeWidth={1.75} /> },
  { id: 'image', label: 'Image', icon: <ImageIcon size={20} strokeWidth={1.75} /> },
];

interface ToolsIconRailProps {
  activeTool: FieldToolId | 'image' | null;
  onToolChange: (tool: FieldToolId | 'image') => void;
  disabled?: boolean;
}

const ToolsIconRailComponent: React.FC<ToolsIconRailProps> = ({
  activeTool,
  onToolChange,
  disabled = false,
}) => {
  const active = activeTool ?? 'select';

  return (
    <nav className="tools-icon-rail" aria-label="Field tools">
      {TOOLS.map((tool) => (
        <ToolRailButton
          key={tool.id}
          tool={tool}
          active={active === tool.id}
          disabled={disabled}
          onToolChange={onToolChange}
        />
      ))}
    </nav>
  );
};

interface ToolRailButtonProps {
  tool: ToolRailItem;
  active: boolean;
  disabled: boolean;
  onToolChange: (tool: FieldToolId | 'image') => void;
}

const ToolRailButton = memo(function ToolRailButton({
  tool,
  active,
  disabled,
  onToolChange,
}: ToolRailButtonProps) {
  const handleClick = useCallback(() => {
    onToolChange(tool.id);
  }, [onToolChange, tool.id]);

  return (
    <button
      type="button"
      disabled={disabled}
      className={`tools-icon-rail-btn ${active ? 'active' : ''}`}
      onClick={handleClick}
      title={
        tool.shortcut
          ? `${tool.label} (${tool.shortcut})`
          : tool.label
      }
      aria-pressed={active}
      aria-label={tool.label}
    >
      {tool.icon}
      <span className="tools-icon-rail-label">{tool.label}</span>
    </button>
  );
});

export const ToolsIconRail = memo(ToolsIconRailComponent);